import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Heart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import NicheFinderGrid from './NicheFinderGrid';

const API = (import.meta.env.VITE_BACKEND_URL || '') + '/api';

/**
 * NicheFinderFavoritesView — vista dedicada de Favoritos (Pacote 5).
 *
 * Busca GET /api/nichos-virais/favorites (objetos completos) e renderiza no
 * mesmo grid/card da descoberta. Desfavoritar remove o card na hora — a lista
 * exibida é filtrada por isFavorited(), sem refetch. "Voltar para descobrir"
 * (onBack) sai da vista.
 */
const NicheFinderFavoritesView = ({
  isPrivacyMode,
  isSuperAdmin,
  visitedList,
  onVisit,
  onDelete,
  onReenrich,
  isFavorited,
  onToggleFavorite,
  onBack,
}) => {
  const { t } = useTranslation();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/nichos-virais/favorites`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setChannels(Array.isArray(data?.channels) ? data.channels : []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Filtra pelos que continuam favoritados — desfavoritar some o card na hora.
  const visibleChannels = useMemo(() => {
    if (typeof isFavorited !== 'function') return channels;
    return channels.filter((c) => isFavorited(c.channel_id));
  }, [channels, isFavorited]);

  const isEmpty = !loading && !error && visibleChannels.length === 0;

  return (
    <div>
      {/* Cabeçalho da vista: título + contagem + voltar. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 14,
          paddingLeft: 14,
          paddingRight: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Heart size={16} style={{ color: 'var(--dp-red-500)' }} fill="var(--dp-red-500)" />
          <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
            {t('viralNiches.favorites_title')}
          </span>
          {!loading && !error && (
            <span style={{ fontSize: 12, color: 'var(--dp-text-tertiary)' }}>
              {visibleChannels.length}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onBack}
          className="dp-btn-secondary"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            height: 36, padding: '0 14px', fontSize: 13, borderRadius: 8,
            fontFamily: 'var(--dp-font)',
          }}
        >
          <ArrowLeft size={14} />
          <span>{t('viralNiches.favorites_back')}</span>
        </button>
      </div>

      {isEmpty ? (
        <div
          className="dp-card"
          style={{
            padding: 32,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            fontFamily: 'var(--dp-font)',
          }}
        >
          <Heart size={28} style={{ color: 'var(--dp-text-tertiary)' }} />
          <div style={{ fontSize: 14, color: 'var(--dp-text-secondary)' }}>
            {t('viralNiches.favorites_empty')}
          </div>
          <button type="button" className="dp-btn-secondary" onClick={onBack}>
            {t('viralNiches.favorites_back')}
          </button>
        </div>
      ) : (
        <NicheFinderGrid
          channels={visibleChannels}
          loading={loading}
          error={error}
          onRetry={load}
          hasActiveFilters={false}
          onClearFilters={onBack}
          hasMore={false}
          loadMore={() => {}}
          visitedList={visitedList}
          isSuperAdmin={isSuperAdmin}
          isPrivacyMode={isPrivacyMode}
          onVisit={onVisit}
          onDelete={onDelete}
          onReenrich={onReenrich}
          isFavorited={isFavorited}
          onToggleFavorite={onToggleFavorite}
        />
      )}
    </div>
  );
};

export default NicheFinderFavoritesView;
